import { CursorPaginatedResponseSchema } from 'notable-common';
import { z } from 'zod';
import { ScenarioEntitySchema } from './entity.js';
/** A form a matching scenario attaches to the patient's previsit, or removes from it. */
export const ScenarioFormSchema = z.object({
    templateId: z.uuid(),
    add: z.boolean().describe('True attaches the form, false removes one already attached.'),
});
/** Follow-up scheduled a fixed time after the visit. */
export const ScenarioPostVisitSchema = z.object({
    duration: z.number(),
    unit: z.string().describe('The unit the duration counts, such as DAY or MONTH.'),
});
/**
 * One scenario: what it does when it matches, and the organizations, practices, providers, and
 * feeds it applies to.
 *
 * A scenario also carries matching criteria, from age bounds to payor plan names to appointment
 * notes, well over a hundred of them. Restating that surface would be a second copy of the rule
 * engine rather than a contract, so `additionalSettings` names the ones a scenario actually sets
 * and the admin UI remains where a rule is read in full.
 */
export const ScenarioSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string(),
    organizationId: z.uuid().nullable().describe('The organization owning the scenario.'),
    entity: ScenarioEntitySchema.nullable().describe('What the scenario is evaluated against: one fires per encounter, per order, or per patient.'),
    isEnabled: z.boolean().describe('Whether the scenario is evaluated at all.'),
    applicableOrganizationIds: z.array(z.uuid()).nullable(),
    applicablePracticeGroupIds: z.array(z.uuid()).nullable(),
    excludedPracticeGroupIds: z.array(z.uuid()).nullable(),
    applicablePracticeIds: z.array(z.uuid()).nullable(),
    excludedPracticeIds: z.array(z.uuid()).nullable(),
    applicableProviderIds: z.array(z.uuid()).nullable(),
    excludedProviderIds: z.array(z.uuid()).nullable(),
    applicableFeedIds: z
        .array(z.uuid())
        .nullable()
        .describe('Feeds whose rows the scenario applies to.'),
    forms: z.array(ScenarioFormSchema).nullable().describe('Previsit forms the scenario changes.'),
    flowDefinitionIds: z.array(z.uuid()).nullable().describe('Flows the scenario starts.'),
    notificationContentIds: z
        .array(z.uuid())
        .nullable()
        .describe('Notification contents the scenario sends.'),
    noteTemplateId: z.uuid().nullable().describe('The note template the scenario applies.'),
    ingestJobKinds: z.array(z.string()).nullable().describe('EHR jobs the scenario requests.'),
    flags: z.array(z.string()).nullable().describe('Flags the scenario puts on the encounter.'),
    postVisits: z.array(ScenarioPostVisitSchema).nullable(),
    additionalSettings: z
        .array(z.string())
        .describe('Names, without values, of the fields this scenario sets that this read does not publish: ' +
        'mostly matching criteria, and a few outputs such as authorization notes. Entries here ' +
        'mean the scenario is configured beyond what this response shows, which may narrow where ' +
        'it applies, widen it, or add an action. They do not say which.'),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    scenarioRuleCreated: z.iso
        .datetime()
        .describe('When the matching rule was created, which is tracked apart from the scenario.'),
    scenarioRuleUpdated: z.iso
        .datetime()
        .describe('When the matching rule last changed. A scenario can be renamed without its rule moving, ' +
        'and its rule can change without the scenario being touched, so compare this to an ' +
        "encounter's date when asked why a scenario did or did not fire."),
});
export const ScenarioListEntrySchema = ScenarioSchema.pick({
    id: true,
    name: true,
    description: true,
    organizationId: true,
    entity: true,
    isEnabled: true,
    applicableOrganizationIds: true,
    applicablePracticeGroupIds: true,
    applicablePracticeIds: true,
    applicableProviderIds: true,
    applicableFeedIds: true,
    excludedPracticeGroupIds: true,
    excludedPracticeIds: true,
    excludedProviderIds: true,
    flowDefinitionIds: true,
    updated: true,
});
export const ScenarioListResponseSchema = CursorPaginatedResponseSchema(ScenarioListEntrySchema);
//# sourceMappingURL=read.js.map